import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import dateTimeService from '../../services/DateTimeService';
import LoadTracker from '../../services/LoadTracker';
import settingsReader from '../../store/readHelpers/SettingsReader';
import sessionReader from '../../store/readHelpers/SessionReader';
import { t } from '../../services/i18nService';
import utilService from '../../services/UtilService';

import styles from './testCover.scss';

export class CoverRow extends React.PureComponent {
    static propTypes = {
        rowKey: PropTypes.string.isRequired,
        rowValue: PropTypes.string.isRequired,
        isHeaderRow: PropTypes.bool,
    };

    render() {
        const { rowKey, rowValue, isHeaderRow } = this.props;
        const classes = isHeaderRow ? `header-row ${rowKey}` : 'row';

        return (
            <div className={classes}>
                <span className="row-key">{t(`testCover.${rowKey}`)}</span>
                <span className="row-value">{rowValue}</span>
            </div>
        );
    }
}

export class CoverDetails extends React.PureComponent {
    static propTypes = {
        deliveryDetails: PropTypes.object.isRequired,
    };

    render() {
        const { deliveryDetails } = this.props;
        const {
            subjectCode, evaluationType, assessmentRunStartTime, assessmentRunEndTime, gradeDeadline, loggedInUser,
        } = deliveryDetails;
        const notSetCaption = t('testCover.valueNotSet');
        const details = {
            subjectCode: subjectCode || notSetCaption,
            evaluationType: evaluationType || notSetCaption,
            assessmentRunStartTime: dateTimeService.formatDateTime(new Date(assessmentRunStartTime)),
            assessmentRunEndTime: dateTimeService.formatDateTime(new Date(assessmentRunEndTime)),
            gradeDeadline: gradeDeadline ? dateTimeService.formatDateTime(new Date(gradeDeadline)) : notSetCaption,
            createdAt: dateTimeService.formatDateTime(),
            createdBy: sessionReader.getPrintParam(sessionReader.printParamNames.PRINT_CREATED_BY) || loggedInUser,
        };

        if (sessionReader.isContextViewOwnDelivery()) {
            delete details.createdBy;
        }

        const rows = Object
            .keys(details)
            .map(key => <CoverRow key={key} rowKey={key} rowValue={details[key]} />);

        return (
            <div className="cover-summary">
                {rows}
            </div>
        );
    }
}

class TestCoverContent extends React.Component {
    static propTypes = {
        deliveryDetails: PropTypes.object.isRequired,
        onReady: PropTypes.func.isRequired,
        direction: PropTypes.string,
    };

    constructor(props) {
        super(props);
        this.state = { logoError: false };
        this.initLoadTracker();
    }

    componentDidMount() {
        if (this.rootElement) {
            this.renderTracker(true);
        }
    }

    componentDidUpdate() {
        if (this.rootElement) {
            this.renderTracker(true);
        }

        // Report image ready if there was an error loading it, and logo element is removed from dom.
        if (this.state.logoError && !this.logoImgElement) {
            this.imageTracker(true);
        }
    }

    initLoadTracker() {
        this.loadTracker = new LoadTracker((success) => {
            this.props.onReady(success);
        }, 'TestCover');

        this.imageTracker = this.loadTracker.addItemToTrack('TestCover > Logo');
        this.renderTracker = this.loadTracker.addItemToTrack('TestCover > render');
        this.loadTracker.setAllItemsAdded();
    }

    shouldAddPageBreak() {
        return !settingsReader.hasSettingValue(sessionReader.printParamNames.INSERT_PAGE_BREAK_BEFORE_QUESTION, false)
            && !sessionReader.isContextViewOwnDelivery();
    }

    onImageLoad = () => {
        utilService.callAfterAnimationFrames(() => {
            this.imageTracker(true);
        });
    };

    onImageLoadError = () => {
        this.setState({ logoError: true });
    };

    render() {
        const { assessmentRunTitle, candidateExternalId, groupName } = this.props.deliveryDetails;
        const pageBreakClass = this.shouldAddPageBreak() ? 'page-break-after' : '';
        const classes = `${styles.testCoverWrapper} ${pageBreakClass}`;

        const optionalAttrs = {};
        if (this.props.direction) {
            optionalAttrs.dir = this.props.direction;
        }

        return (
            <div
                className={classes}
                ref={(element) => { this.rootElement = element; }}
                {...optionalAttrs}
            >
                <div className="cover">
                    <div className="header-row logo">
                        {
                            this.state.logoError ?
                                null
                                :
                                <img
                                    alt={window.marketplaceName}
                                    className="mpLogo"
                                    onError={this.onImageLoadError}
                                    onLoad={this.onImageLoad}
                                    ref={(element) => { this.logoImgElement = element; }}
                                    src={window.marketplaceLogo}
                                />
                        }
                    </div>
                    <CoverRow
                        key="candidateExternalId"
                        rowKey={candidateExternalId ? 'candidateExternalId' : 'groupName'}
                        rowValue={candidateExternalId || groupName}
                        isHeaderRow
                    />
                    <CoverRow
                        key="assessmentRunTitle"
                        rowKey="assessmentRunTitle"
                        rowValue={assessmentRunTitle}
                        isHeaderRow
                    />
                    <CoverDetails {...this.props} />
                </div>
            </div>
        );
    }
}

function mapStateToProps(state) {
    const { ui: { direction } } = state;
    return { direction };
}

const TestCover = connect(mapStateToProps)(TestCoverContent);
export default TestCover;
